import Axios from 'axios';
import * as R from 'ramda';

import { loginRoute } from 'App/common/routeLookup';
import TokenManagerService from 'App/services/TokenManagerService';
import errorHandler from './errorHandler';
import getData from './getData';

let isLoggingOut = false;

function resetLogoutGuard() {
    isLoggingOut = false;
}

function authRequestInterceptor(config) {
    const token = TokenManagerService.getAccessToken();
    if (token) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.headers.Accept) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Accept = 'application/json';
    }

    return config;
}

function forceLogout(error) {
    if (!isLoggingOut) {
        isLoggingOut = true;
        // Local-only: this tab concluded its own session is dead. Don't broadcast —
        // that's reserved for explicit logout (see TokenManagerService.clearTokens).
        TokenManagerService.clearTokensLocal();
        window.location.assign(loginRoute);
    }
    return Promise.reject(error);
}
/**
 * Response error interceptor → applies errorHandler
 */
function errorResponseInterceptor(axiosInstance, shouldShowToastFn) {
    return async (error) => {
        const originalRequest = error.config;

        // If we're already redirecting to logout, reject immediately
        if (isLoggingOut) {
            return Promise.reject(error);
        }

        /**
         * if we have a 401,
         * haven't already retried
         * and we currently have a token
         */
        if (error.response?.status === 401) {
            if (originalRequest._retry) {
                return forceLogout(error);
            }

            if (TokenManagerService.getAccessToken()) {
                originalRequest._retry = true;
                try {
                    const newToken = await TokenManagerService.refreshAccessToken(true);
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    const refreshStatus = refreshError?.response?.status;
                    const isDefinitive = refreshStatus === 401 || !TokenManagerService.getAccessToken();
                    if (isDefinitive) {
                        return forceLogout(refreshError);
                    }
                    return Promise.reject(refreshError);
                }
            }
        }
        const hasToken = Boolean(TokenManagerService.getAccessToken());
        if (hasToken) {
            errorHandler(error, hasToken ? shouldShowToastFn(error) : false);
        }
        return Promise.reject(error);
    };
}

/**
 * @callback shouldShowToastFn
 * @param {Error} The error message
 * @return {Boolean} Whether or not to show the toast message
 */

/**
 *
 * @param config
 * @param {shouldShowToastFn} shouldShowToastFn
 * @returns {AxiosInstance}
 */
function createAxios(config, { shouldShowToastFn = R.T } = {}) {
    const axios = Axios.create(config);

    axios.interceptors.request.use(authRequestInterceptor);
    axios.interceptors.response.use(getData, errorResponseInterceptor(axios, shouldShowToastFn));

    return axios;
}

export { resetLogoutGuard };
export default createAxios;
